import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import moment from "moment";
import useMedia from "utils/hooks/useMedia";
import {
  BOTTOM_NAVIGATOR,
  HIDE_INTERCOM_LAUNCHER_BREAKPOINT,
  IS_WEB_VIEW,
} from "common/constants";
import { useIsOnboarding } from "utils/hooks/misc";
import {
  getAppcuesAndIntercomUserProperties,
  getIntercomAppId,
} from "@shared/analytics/utils";
import { useGetUser, useSharedIntercom } from "@shared/utils/hooks/misc";
import { sharedLib } from "@shared/services/shared.service";
import _Intercom, { boot, shutdown, update } from "@intercom/messenger-js-sdk";
import { useEffect } from "react";
import { setIntercomLauncherVisibility } from "utils/misc.tsx";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";

const Intercom = (_Intercom as any).default || _Intercom;
const getLastRequestAt = () => moment.utc().locale("en").format("MMM Do, YY");

const useIntercom = () => {
  const location = useLocation();
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const isOnboarding = useIsOnboarding();
  const mobileHideLauncher = useMedia(
    [
      `(max-width: ${
        isOnboarding ? BOTTOM_NAVIGATOR : HIDE_INTERCOM_LAUNCHER_BREAKPOINT
      })`,
    ],
    [true],
    false
  );
  const { isWhitelabel } = useBranding();
  const appId = getIntercomAppId(`${import.meta.env.VITE_APP_ENVIRONMENT}`);
  const { isLoading } = useGetUser();
  const showLauncher =
    !isWhitelabel && ((!user && !isLoading) || (user && !mobileHideLauncher));

  const startIntercom = () => {
    if (!IS_WEB_VIEW) {
      Intercom({
        app_id: appId,
        hide_default_launcher: !showLauncher,
      });
    }
  };

  useEffect(() => {
    startIntercom();
  }, []);

  const getIntercomUserProperties = () => {
    if (user) {
      const customAttributes = getAppcuesAndIntercomUserProperties(
        user,
        isOnboarding
      );
      return {
        user_id: user.user_uuid,
        email: user.email,
        user_hash: user.uuid_user_hash,
        ...customAttributes,
      };
    }
    return {};
  };
  const userIntercomProperties = getIntercomUserProperties();

  const isInitialized = useSharedIntercom({
    logout: () => {
      shutdown();
      startIntercom();
    },
    update: () => {
      const userProperties = getIntercomUserProperties();
      update({
        ...userProperties,
        lastRequestAt: getLastRequestAt(),
      });
    },
    login: () => {
      if (appId) {
        const userProperties = getIntercomUserProperties();
        boot({
          app_id: getIntercomAppId(`${import.meta.env.VITE_APP_ENVIRONMENT}`),
          ...userProperties,
          hide_default_launcher: !showLauncher,
        });
      }
    },
    skip: IS_WEB_VIEW,
    isReady: !isLoading,
  });

  useEffect(() => {
    if (IS_WEB_VIEW && !isLoading) {
      setIntercomLauncherVisibility(!!showLauncher);
    }
  }, [showLauncher, isLoading]);

  useEffect(() => {
    if (isInitialized) {
      update({ hide_default_launcher: !showLauncher });
    }
  }, [isInitialized, showLauncher]);

  useEffect(() => {
    if (isInitialized) {
      update({
        ...userIntercomProperties,
        lastRequestAt: getLastRequestAt(),
        hide_default_launcher: !showLauncher,
      });
    }
  }, [location.pathname]);
};

export default useIntercom;
